import { useState } from "react";
import { Search, ChevronDown, Users, Activity } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

interface League {
  id: string;
  name: string;
  max_teams: number;
  created_at: string;
}

interface Team {
  id: string;
  user_id: string;
  league_id: string;
  owner_name?: string;
}

interface TeamPageHeaderProps {
  selectedLeague: League | null;
  selectedTeam: Team | null;
  userLeagues: League[];
  teamsInLeague: Team[];
  onLeagueSelect: (league: League) => void;
  onTeamSelect: (team: Team) => void;
}

export function TeamPageHeader({
  selectedLeague,
  selectedTeam,
  userLeagues,
  teamsInLeague,
  onLeagueSelect,
  onTeamSelect,
}: TeamPageHeaderProps) {
  const [leagueSearchOpen, setLeagueSearchOpen] = useState(false);
  const [leagueSearchValue, setLeagueSearchValue] = useState("");

  const getLeagueStatusChip = (league: League) => {
    const memberCount = teamsInLeague.length;
    const isActive = memberCount > 0;
    
    return (
      <Badge 
        variant={isActive ? "default" : "secondary"}
        className="text-xs"
      >
        {memberCount}/{league.max_teams}
      </Badge>
    );
  };

  const getTeamDisplayName = (team: Team) => {
    return team.owner_name ? `${team.owner_name}'s Team` : "Team";
  };

  return (
    <div className="flex items-center justify-between">
      <h1 className="text-3xl font-bold">Teams</h1>
      
      <div className="flex items-center gap-3">
        {/* League Switcher */}
        <Popover open={leagueSearchOpen} onOpenChange={setLeagueSearchOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              role="combobox"
              aria-expanded={leagueSearchOpen}
              className="min-w-[200px] justify-between"
            >
              {selectedLeague ? (
                <div className="flex items-center gap-2">
                  <Avatar className="h-5 w-5">
                    <AvatarFallback className="text-xs">
                      {selectedLeague.name.charAt(0)}
                    </AvatarFallback>
                  </Avatar>
                  <span className="truncate">{selectedLeague.name}</span>
                  {getLeagueStatusChip(selectedLeague)}
                </div>
              ) : (
                "Select league..."
              )}
              <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[300px] p-0" align="end">
            <Command>
              <CommandInput
                placeholder="Search leagues..."
                value={leagueSearchValue}
                onValueChange={setLeagueSearchValue}
              />
              <CommandList>
                <CommandEmpty>No leagues found.</CommandEmpty>
                <CommandGroup>
                  {userLeagues.map((league) => (
                    <CommandItem
                      key={league.id}
                      value={league.name}
                      onSelect={() => {
                        onLeagueSelect(league);
                        setLeagueSearchOpen(false);
                        setLeagueSearchValue("");
                      }}
                    >
                      <div className="flex items-center gap-2 flex-1">
                        <Avatar className="h-6 w-6">
                          <AvatarFallback className="text-xs">
                            {league.name.charAt(0)}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1">
                          <div className="font-medium">{league.name}</div>
                          <div className="text-sm text-muted-foreground">
                            {teamsInLeague.length} members
                          </div>
                        </div>
                        <Badge variant="secondary" className="text-xs">
                          Active
                        </Badge>
                      </div>
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>

        {/* Team Selector */}
        {selectedLeague && teamsInLeague.length > 0 && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="min-w-[160px] justify-between">
                {selectedTeam ? (
                  <div className="flex items-center gap-2">
                    <Users className="h-4 w-4" />
                    <span className="truncate">
                      {getTeamDisplayName(selectedTeam)}
                    </span>
                  </div>
                ) : (
                  "Select team..."
                )}
                <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-[200px]">
              {teamsInLeague.map((team) => (
                <DropdownMenuItem
                  key={team.id}
                  onSelect={() => onTeamSelect(team)}
                  className="flex items-center gap-2"
                >
                  <Users className="h-4 w-4" />
                  <span className="flex-1 truncate">
                    {getTeamDisplayName(team)}
                  </span>
                  {team.user_id === selectedTeam?.user_id && (
                    <Activity className="h-3 w-3 text-primary" />
                  )}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    </div>
  );
}